import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  isLoadingSelector,
  isReadySelector,
  gameDataSelector,
  gameStateSelector,
  gameIdSelector,
  stateSelector,
} from 'store/selectors';
import Loader from 'components/Loader';
import events from 'store/events';
import {
  getSelected,
  getHighlighted,
  clueEntry,
} from 'store/helper';
import successMp3 from 'assets/success.mp3';
import misc from 'misc';

const GameContext = React.createContext();

const GameContextProvider = ({ id, children, source }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const state = useSelector(stateSelector);
  const isLoading = useSelector(isLoadingSelector);
  const isReady = useSelector(isReadySelector);
  const gameId = useSelector(gameIdSelector);
  const gameData = useSelector(gameDataSelector);
  const gameState = useSelector(gameStateSelector);
  const letters = useMemo(() => isReady &&
    misc.chunk(gameData.grid, gameData.size.cols),
    [isReady, gameData]
  );
  const positions = useMemo(() => isReady &&
    misc.chunk(gameData.grid.map((_, i) => i), gameData.size.cols),
    [isReady, gameData]
  );
  const selected = useMemo(() => isReady &&
    getSelected(state),
    [isReady, state]
  );
  const highlighted = useMemo(() => isReady &&
    getHighlighted(state),
    [isReady, state]
  );
  const clueMap = useMemo(() => isReady && ({
    across: Object.fromEntries(gameData.clues.across
      .map(clue => clueEntry(clue, true))
    ),
    down: Object.fromEntries(gameData.clues.down
      .map(clue => clueEntry(clue))
    ),
  }), [isReady, gameData]);
  const selectedClues = useMemo(() => isReady && ({
    across: gameData.gridnums[
      gameData.moves[gameState.cursor.position]?.clues.current[0]
    ],
    down: gameData.gridnums[
      gameData.moves[gameState.cursor.position]?.clues.current[1]
    ],
  }), [isReady, gameData, gameState]);
  const isClean = useMemo(() => isReady &&
    !gameState.guesses.join('').trim().length,
    [isReady, gameState]
  );
  const isComplete = useMemo(() => isReady &&
    gameState.guesses.join('').replace(/ /g, '.') === gameData.grid.join(''),
    [isReady, gameState, gameData]
  );
  const successSound = useMemo(() => new Audio(successMp3), []);
  const url = useMemo(() => location.pathname.split('/'), [location]);
  const urlGameId = useMemo(() => url[url.length - 1], [url]);
  const select = ({ position, across }) => dispatch({
    type: events.handleSelect, position, across
  });
  const handleKeyDown = useCallback(e => {
    if (e.preventDefault && (
      e.key === 'Tab' || e.key === ' '
      || e.key === 'ArrowUp' || e.key === 'ArrowDown'
      || e.key === 'ArrowRight' || e.key === 'ArrowLeft')
    ) e.preventDefault();
    dispatch({
      type: events.handleInput,
      key: e.key,
      shift: e.shiftKey,
      meta: e.metaKey,
      ctrl: e.ctrlKey,
    });
  }, [dispatch]);
  useEffect(() => {
    if (gameId === urlGameId && isReady && isComplete) successSound.play()
      .catch(e => console.warn('successSound unplayable:', e));
  }, [isReady, isComplete, location, urlGameId, gameId, successSound]);
  useEffect(() => {
    dispatch({ type: events.fetchGame, id, source });
  }, [dispatch, id, source]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown]);


  return (
    <GameContext.Provider value={{
      ...gameData,
      ...gameState,
      actions: {
        select
      },
      gameId,
      isReady,
      isClean,
      isComplete,
      letters,
      positions,
      selected,
      selectedClues,
      clueMap,
      highlighted,
      handleKeyDown,
    }}>
      <Loader
        isLoading={isLoading}
        isReady={isReady}
      >
        {children}
      </Loader>
    </GameContext.Provider>
  )
}

export { GameContextProvider, GameContext };
